import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useReducedMotion } from "../lib/useReducedMotion";

/**
 * Magnetic CTA — the button gently translates toward the cursor when the
 * cursor enters the surrounding "magnet zone" (~60px radius). Always renders
 * a normal button on touch devices / reduced motion.
 */
export const MagneticButton = ({
    children,
    onClick,
    type = "button",
    disabled = false,
    radius = 60,
    strength = 0.18,
    className = "",
    "data-testid": testId,
    ariaLabel,
}) => {
    const reduced = useReducedMotion();
    const ref = useRef(null);
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const sx = useSpring(x, { stiffness: 220, damping: 18, mass: 0.6 });
    const sy = useSpring(y, { stiffness: 220, damping: 18, mass: 0.6 });
    const tx = useTransform(sx, (v) => v);
    const ty = useTransform(sy, (v) => v);

    const onMove = (e) => {
        if (reduced || disabled || !ref.current) return;
        const r = ref.current.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        const dist = Math.hypot(dx, dy);
        if (dist > radius) {
            x.set(0);
            y.set(0);
            return;
        }
        x.set(dx * strength);
        y.set(dy * strength);
    };

    const onLeave = () => {
        x.set(0);
        y.set(0);
    };

    return (
        <motion.button
            ref={ref}
            type={type}
            onClick={onClick}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            disabled={disabled}
            style={{ x: tx, y: ty }}
            className={className}
            data-testid={testId}
            aria-label={ariaLabel}
        >
            {children}
        </motion.button>
    );
};

export default MagneticButton;
